// 2026-10-08 (Manak, "bulk reassign yes") — shared bits for bulk reassign on the
// web Leads page. Server: backend/src/modules/leads/bulk-reassign.routes.ts.
import { api } from '@/utils/api';

export const BULK_REASSIGN_URL = '/lead-integrations/lead-imports/bulk-reassign';
export const BULK_REASSIGN_MAX = 200;
export const UNDO_SECONDS = 10;

export type SkipReason =
  | 'not_visible' | 'duplicate' | 'won' | 'lost' | 'with_dealer' | 'dealer_prospect' | 'already_owner' | 'changed_since';

export const SKIP_LABEL: Record<SkipReason, string> = {
  not_visible: 'you cannot see',
  duplicate: 'duplicate',
  won: 'won',
  lost: 'lost',
  with_dealer: 'with a dealer',
  dealer_prospect: 'dealer prospect',
  already_owner: 'already with this person',
  changed_since: 'changed meanwhile',
};

export interface PickedLead { id: string; name?: string | null; assignedToName?: string | null }
export interface PickableEmployee { id: string; firstName: string; lastName: string; userId?: string | null; designation?: string | null }

export interface BulkLeadResult { leadId: string; name: string | null; status: 'ok' | 'skipped'; reason?: SkipReason }
export interface BulkReassignResult {
  requestId: string;
  toUser: { id: string; name: string };
  results: BulkLeadResult[];
  ok: number;
  skipped: number;
  undoUntil: string | null;
}

export function ownerBreakdownText(leads: PickedLead[]): string {
  const counts = new Map<string, number>();
  for (const l of leads) {
    const k = (l.assignedToName ?? '').trim() || 'Unassigned';
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => (a[0] === 'Unassigned' ? 1 : b[0] === 'Unassigned' ? -1 : b[1] - a[1] || a[0].localeCompare(b[0])))
    .map(([name, n]) => `${name} (${n})`)
    .join(' · ');
}

export function skipSummary(results: BulkLeadResult[]): Array<{ reason: SkipReason; label: string; count: number }> {
  const m = new Map<SkipReason, number>();
  for (const r of results) if (r.status === 'skipped' && r.reason) m.set(r.reason, (m.get(r.reason) ?? 0) + 1);
  return [...m.entries()].map(([reason, count]) => ({ reason, label: SKIP_LABEL[reason] ?? reason, count }));
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function newRequestId(): string {
  const c = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `br-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function fetchBulkEligibility(): Promise<boolean> {
  try {
    const r = await api.get(`${BULK_REASSIGN_URL}/eligibility`);
    return r?.data?.data?.canBulkReassign === true;
  } catch {
    return false;
  }
}

export async function postBulkReassign(body: {
  leadIds: string[]; toUserId: string; reason: string; moveFollowUps: boolean; requestId: string; dryRun: boolean;
}): Promise<BulkReassignResult> {
  const r = await api.post(BULK_REASSIGN_URL, body);
  return r.data.data as BulkReassignResult;
}

export async function postBulkUndo(requestId: string): Promise<BulkReassignResult> {
  const r = await api.post(`${BULK_REASSIGN_URL}/undo`, { requestId });
  return r.data.data as BulkReassignResult;
}

export function errorText(e: unknown): string {
  const any = e as any;
  return any?.response?.data?.error || any?.message || 'Something went wrong';
}
